import { DomainError } from './errors';
import type {
  ContainerTrackingMode,
  EquipmentKind,
  FoodContainer,
  StorageAppliance,
} from './model';

export interface StoragePolicy {
  family: 'freezer' | 'fridge' | 'pantry';
  trackingMode: ContainerTrackingMode;
  defaultDeclaredTemperatureC: number | null;
  storageDateLabel:
    | 'Date de congélation'
    | 'Date d’entrée au frigo'
    | 'Date d’entrée dans la réserve';
  allowsFreezerHorizon: boolean;
}

export const storagePolicyFor = (equipmentKind: EquipmentKind): StoragePolicy => {
  if (equipmentKind === 'fridge') {
    return {
      family: 'fridge',
      trackingMode: 'internal-only',
      defaultDeclaredTemperatureC: 4,
      storageDateLabel: 'Date d’entrée au frigo',
      allowsFreezerHorizon: false,
    };
  }
  if (equipmentKind === 'pantry') {
    return {
      family: 'pantry',
      trackingMode: 'internal-only',
      defaultDeclaredTemperatureC: null,
      storageDateLabel: 'Date d’entrée dans la réserve',
      allowsFreezerHorizon: false,
    };
  }
  return {
    family: 'freezer',
    trackingMode: 'visible-number',
    defaultDeclaredTemperatureC: equipmentKind === 'unknown' ? null : -18,
    storageDateLabel: 'Date de congélation',
    allowsFreezerHorizon: true,
  };
};

export const isFridge = (appliance: StorageAppliance | null | undefined): boolean =>
  appliance?.equipmentKind === 'fridge';

export const isPantry = (appliance: StorageAppliance | null | undefined): boolean =>
  appliance?.equipmentKind === 'pantry';

export const sharesStorageFamily = (
  sourceKind: EquipmentKind,
  destinationKind: EquipmentKind,
): boolean => storagePolicyFor(sourceKind).family === storagePolicyFor(destinationKind).family;

export const assertSameStorageFamily = (
  sourceKind: EquipmentKind,
  destinationKind: EquipmentKind,
): void => {
  if (!sharesStorageFamily(sourceKind, destinationKind)) {
    throw new DomainError(
      'Un changement de famille de stockage doit être enregistré comme une sortie puis une nouvelle entrée.',
      'INVALID_STATE',
    );
  }
};

export const containerReference = (container: FoodContainer): string =>
  container.serialNumber == null ? 'Aliment sans numéro' : `N° ${container.serialNumber}`;

export const assertNoFreezerHorizonForInternalContainer = (
  container: Pick<FoodContainer, 'trackingMode' | 'qualityHorizonOn' | 'horizonRule'>,
): void => {
  if (
    container.trackingMode === 'internal-only' &&
    (container.horizonRule != null || container.qualityHorizonOn != null)
  ) {
    throw new DomainError(
      'Un aliment stocké sans numéro ne peut pas conserver un horizon de congélation.',
      'INVALID_STATE',
    );
  }
};

export const assertTrackingMatchesEquipment = (
  container: Pick<
    FoodContainer,
    | 'trackingMode'
    | 'serialNumber'
    | 'qualityHorizonOn'
    | 'horizonRule'
    | 'purchasedOn'
    | 'openedOn'
    | 'useFirstOn'
  >,
  equipmentKind: EquipmentKind,
): void => {
  const policy = storagePolicyFor(equipmentKind);
  if (container.trackingMode !== policy.trackingMode) {
    throw new DomainError(
      'Un passage entre un stockage numéroté et un stockage sans numéro doit être enregistré ' +
        'comme une nouvelle entrée.',
      'INVALID_STATE',
    );
  }
  if (
    (policy.trackingMode === 'visible-number' && container.serialNumber == null) ||
    (policy.trackingMode === 'internal-only' && container.serialNumber != null)
  ) {
    throw new DomainError('Le mode d’identification de cet aliment est incohérent.', 'INVALID_STATE');
  }
  assertNoFreezerHorizonForInternalContainer(container);
  if (
    policy.family === 'freezer' &&
    [container.purchasedOn, container.openedOn, container.useFirstOn].some((value) => value != null)
  ) {
    throw new DomainError(
      'Les dates d’achat, d’ouverture et le repère choisi sont réservés aux stockages sans numéro.',
      'INVALID_STATE',
    );
  }
};
